/**
 * Heatmap consent banner.
 *
 * Cookieless pageview analytics run without consent (see analytics.ts); this
 * banner asks only for the more invasive heatmap / session-recording feature.
 * Shown on first visit (works on public pages too) until the user accepts or
 * declines. The choice can be changed later from Profile settings. Consent is
 * persisted in localStorage.
 */
import React, { useEffect, useState } from 'react';
import { Snackbar, Paper, Box, Typography, Button, Slide } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  getHeatmapConsent,
  setHeatmapConsent,
  HEATMAP_CONSENT_EVENT,
} from '@/services/analytics';

const HeatmapConsentBanner: React.FC = () => {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Only prompt when the user has not decided yet.
    setOpen(getHeatmapConsent() === null);

    // Hide if the choice is made elsewhere (e.g. the Settings toggle).
    const onChange = () => setOpen(getHeatmapConsent() === null);
    window.addEventListener(HEATMAP_CONSENT_EVENT, onChange);
    return () => window.removeEventListener(HEATMAP_CONSENT_EVENT, onChange);
  }, []);

  const decide = (value: 'granted' | 'denied') => {
    setHeatmapConsent(value);
    setOpen(false);
  };

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      TransitionComponent={Slide}
    >
      <Paper elevation={6} sx={{ p: 2, maxWidth: 520, m: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          {t('consent.heatmap.title', 'Help us improve Saqia')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t(
            'consent.heatmap.description',
            'We use privacy-friendly, cookieless analytics for anonymous usage stats. With your permission we also record anonymized interaction heatmaps (clicks and scrolling) to understand how the app is used. You can change this anytime in your profile settings.'
          )}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button size="small" onClick={() => decide('denied')}>
            {t('consent.heatmap.decline', 'Decline')}
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => decide('granted')}
          >
            {t('consent.heatmap.accept', 'Accept')}
          </Button>
        </Box>
      </Paper>
    </Snackbar>
  );
};

export default HeatmapConsentBanner;
